import { Injectable, EventEmitter } from "@angular/core";

import * as _ from "lodash";
import { Subscription } from "rxjs";

import {
  Dialog,
  DialogSettings,
  DialogAlertParams,
  DialogConfirmParams,
  DialogConfirmCallback,
  DialogPromptParams,
  DialogPromptCallback,
  DialogStringFunction
} from "./dialog.base";
import { DialogBaseService } from "./dialog.base.service";
import { FormValues, FormSchemaData } from "../form/form.interface";

import { DialogLoadingComponent } from "./components/loading/loading.component";
import { DialogAlertComponent } from "./components/alert/alert.component";
import { DialogConfirmComponent } from "./components/confirm/confirm.component";
import { DialogPromptComponent } from "./components/prompt/prompt.component";
import { DialogViewerComponent } from "./components/viewer/viewer.component";

/**
 * ServiÃ§o que inicializa e exibe um modal a partir de um componente
 *
 * Example

```ts
private dialog: Dialog;
private openModal() {
  if(!this.dialog) {
    this.dialog = this.dialogService.create(TesteModal, {
      // Dialog params (Opcional)
      teste: {
        'its work?': true
      }
    }, {
      // Dialog settings (Opcional)
      backdrop: {                             // Backdrop settings (Opcional)
        onClick: 'close'                      // Opcional: 'close', 'dismiss', 'none' (default: close)
      },
      header: {                               // Header settings (Opcional)
        title: 'Teste modal',                 // ObrigatÃ³rio
        subtitle: 'Isso Ã© apenas um teste',   // Opcional
        closable: true,                       // Opcional (default: true)
        closeClick: 'close'                   // Opcional: 'close', 'dismiss' (default: close)
      }
    });
    // Quando o modal Ã© aberto
    this.dialog.onOpen.subscribe(() => console.log("Modal opened"));
    // Quando o dialog Ã© ocultado
    this.dialog.onDismiss.subscribe(() => console.log("Modal dismissed"));
    // Quando o dialog Ã© destruido
    this.dialog.onClose.subscribe(() => console.log("Modal closed"));
    // Quando o dialog retorna um valor
    this.dialog.onReturnValue.subscribe((response) => console.log("Modal response:", response));
  }
  this.dialog.open();
}
```

 *
 */
@Injectable()
export class DialogService {
  constructor(private _baseService: DialogBaseService) {}

  public get onShow(): EventEmitter<Dialog> {
    return this._baseService.onShow;
  }
  public get onHide(): EventEmitter<Dialog> {
    return this._baseService.onHide;
  }

  public get visibleDialogs(): Array<Dialog> {
    return this._baseService.visibleDialogs;
  }

  public get dialogs(): Array<Dialog> {
    return this._baseService.dialogs;
  }

  public create(
    component: any,
    params?: any,
    settings?: DialogSettings
  ): Dialog {
    return this._baseService.create(component, params, settings);
  }

  public open(component: any, params?: any, settings?: DialogSettings): Dialog {
    return this._baseService.open(component, params, settings);
  }

  public loading(message: DialogStringFunction | string): Dialog {
    return this.open(
      DialogLoadingComponent,
      {
        message
      },
      {
        size: "xs",
        backdrop: { onClick: "none" }
      }
    );
  }

  public alert(params: DialogAlertParams): Dialog {
    let size = params.size ? params.size : "xs";
    delete params.size;
    return this.open(DialogAlertComponent, params, { size });
  }

  public confirm(
    params: DialogConfirmParams,
    callback: DialogConfirmCallback
  ): Dialog {
    let size = params.size ? params.size : "xs";
    delete params.size;
    let dialog = this.open(DialogConfirmComponent, params, {
      size,
      backdrop: { onClick: "none" }
    });
    const subscription: Subscription = dialog.onReturnValue.subscribe(
      (response: boolean) => {
        subscription.unsubscribe();
        callback(response);
        dialog.close();
      }
    );
    return dialog;
  }

  public prompt(
    params: DialogPromptParams,
    callback: DialogPromptCallback
  ): Dialog {
    let size = params.size ? params.size : "xs";
    delete params.size;
    if (!_.isArray(params.schema))
      params.schema = [<FormSchemaData>params.schema];
    let dialog = this.open(DialogPromptComponent, params, {
      size,
      backdrop: { onClick: "none" }
    });
    const subscription: Subscription = dialog.onReturnValue.subscribe(
      (response: FormValues) => {
        subscription.unsubscribe();
        callback(response);
        dialog.close();
      }
    );
    return dialog;
  }

  public openViewer(src: string, type: "image" | "video" = "image"): Dialog {
    return this.open(
      DialogViewerComponent,
      { src, type },
      {
        size: "responsive",
        keyboard: {
          onEscape: "close"
        },
        fit_to_screen: false,
        wrapper_class: "uex-dialog-viewer-wrapper",
        dialog_class: "uex-dialog-viewer-dialog",
        content_class: "uex-dialog-viewer-content",
        backdrop_class: "uex-dialog-viewer-backdrop"
      }
    );
  }
}
